import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { getUserFullName } from '~src/interfaces/user.interface';
import { PageContentContainer } from '~src/styles/containers.sc';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import { routes } from '~src/routingConfig';
import {
  fetchImportStatus,
  logError,
  ImportStatusResp,
  fetchPreviewList,
} from '~src/services/fetch/import.service';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import NavArrowLnk from '~src/shared/buttons/NavArrowLnk';
import { StoreI } from '~src/rootReducer';
import { AttributeI } from '~src/interfaces/attributes.types';
import { translateContent } from '~src/services/translationServices';

import { SummaryTile, ContentContainer, UsersList } from './importSummary.sc';
import PageHeader from './PageHeader';

interface StateProps {
  customAttrs: { [key: string]: AttributeI };
}

type Props = StateProps & RouteComponentProps;

const ImportSummary: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [statsData, setStatsData] = useState<ImportStatusResp['stats']>();
  const [failedUsersList, setFailedUsersList] = useState<FailedUserPreviewItemI[]>([]);
  const { importId } = props.match.params as { importId: string };

  useEffect(() => {
    getImportStatus();
  }, []);

  const getImportStatus = async () => {
    try {
      const { data } = await fetchImportStatus(importId, { include: 'item_stats' });
      if (data.stats && data.stats.failed_items) {
        await getFailedUsers();
      }
      setStatsData(data.stats);
    } catch (err) {
      logError('Error on getting import status', '' + err, 'ImportSummary.tsx');
    }
    setLoadingSummary(false);
  };

  const getFailedUsers = async () => {
    try {
      const { data } = await fetchPreviewList({
        'filter.import': importId,
        'filter.has_errors': true,
        per_page: 1000,
      });
      const failedUsers: FailedUserPreviewItemI[] = data.items.map(
        ({ import_data, existing_data, errors }) => ({
          name: getUserFullName({
            firstname: import_data.firstname || existing_data!.firstname,
            lastname: import_data.lastname || existing_data!.lastname,
          }),
          email: import_data.email,
          errors: errors
            ? errors
                .map((err) => {
                  if (err.indexOf('attributes_validation_attributes') !== -1) {
                    const components = err.split('.');
                    const attr = props.customAttrs[components[components.length - 1]];
                    if (attr !== undefined) {
                      const attrReplacement = translateContent(attr.name);
                      return t(`x.error.import.${components[0]}`).replace('%attribute%', attrReplacement);
                    }
                  }
                  return t(`x.error.import.${err}`);
                })
                .join(', ')
            : '',
        }),
      );
      setFailedUsersList(failedUsers);
    } catch (err) {
      logError('Error on getting preview import items', '' + err, 'ImportSummary.tsx');
    }
  };

  return (
    <>
      <PageHeader
        title={{ key: 'x.company.import.summary.header_text' }}
        descr={{ key: 'x.company.import.summary.header_description' }}
      >
        <Link to={routes.company.settings} style={{ textDecoration: 'none' }}>
          <NavArrowLnk arrOnRight arrowDir="right">
            <span data-translation="x.company.import.summary.header_link_text">
              {t('x.company.import.summary.header_link_text')}
            </span>
          </NavArrowLnk>
        </Link>
      </PageHeader>
      {loadingSummary ? (
        <LoadingSpinner marginTop={60} />
      ) : (
        <PageContentContainer>
          <SummaryTile>
            <TileHeader
              title={t('x.company.import.summary_title')}
              titleKey="x.company.import.summary_title"
              border
            />
            {statsData && (
              <ContentContainer>
                <div className="success">
                  {t('x.company.import.summary.success_text', { count: statsData.successful_items })}
                </div>
                {!!statsData.failed_items && (
                  <>
                    <div className="fail">
                      {t('x.company.import.summary.failed_count_text', { count: statsData.failed_items })}
                    </div>
                    <UsersList>
                      {failedUsersList.map((user) => (
                        <li key={user.name}>
                          {user.name} ({user.email}) - <span>{user.errors}</span>
                        </li>
                      ))}
                    </UsersList>
                  </>
                )}
              </ContentContainer>
            )}
          </SummaryTile>
          <TextBtnPageFooter
            t={t}
            infoKey="x.company.import.summary_footer_text"
            btnKey="x.company.import.summary_footer_button"
            linkUrl={routes.company.users}
          />
        </PageContentContainer>
      )}
    </>
  );
};
const mapState2Props = ({ attributes }: StoreI): StateProps => ({
  customAttrs: attributes.byId,
});

export default connect(mapState2Props)(ImportSummary);

interface FailedUserPreviewItemI {
  name: string;
  email: string;
  errors: string;
}
